import { Component, Suspense, lazy, useCallback, useEffect, useMemo, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Link, Navigate, Outlet, RouterProvider, createBrowserRouter, useLocation, useRouteError, type RouteObject } from "react-router";
import { ApiError, setBusinessHandle } from "./api";
import { BusinessProvider, rememberBusiness, useBusiness, useOverviewQuery } from "./business";
import { ToastHost } from "./editor";
import { AccountPage } from "./pages/account";
import { AuthCard, ForgotPasswordPage, SignInPage, SignUpPage, safeReturn } from "./pages/auth";
import { HomePage } from "./pages/home";
import { InvitePage, NewBusinessPage, WelcomePage } from "./pages/outer";
import { SettingsPage } from "./pages/settings";
import { SessionProvider, go, useSession } from "./session";
import { OuterShell, Shell } from "./shell";
import { SOLUTION_ROUTES } from "./solutions/routes";
import { Card, Page, setBusinessClock } from "./ui";

/**
 * Two routers, like two apps on one address: one above any business (log in, your login, a new business, invites)
 * and one inside a business, whose addresses start at /<business> (its basename) so every page links to "/events"
 * and lands at /<business>/events. Going from one to the other is a full load, the way switching stores is.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

/** Mivio's own pages for approvals and the agents, loaded when first opened. */
const ApprovalsPage = lazy(() => import("./pages/agents").then((m) => ({ default: m.ApprovalsPage })));
const ApprovalPage = lazy(() => import("./pages/agents").then((m) => ({ default: m.ApprovalPage })));
const AgentsPage = lazy(() => import("./pages/agents-staff").then((m) => ({ default: m.AgentsPage })));
const AgentsHistoryPage = lazy(() => import("./pages/agents-history").then((m) => ({ default: m.HistoryPage })));
const GuardrailsPage = lazy(() => import("./pages/agents-settings").then((m) => ({ default: m.GuardrailsPage })));
const later = (page: ReactNode) => <Suspense fallback={<div className="loading">Loading…</div>}>{page}</Suspense>;

/** First address segments that are Mivio's own pages, never a business. */
const OUTER = new Set(["", "sign-in", "sign-up", "forgot-password", "invite", "account", "new"]);
const HANDLE = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){1,46}[a-z0-9]$/;

export function NotFoundPage() {
  return (
    <Page title="Not found">
      <Card>
        <p className="acct-plain">
          There's nothing at this address.{" "}
          <Link className="link" to="/">
            Back to Home
          </Link>
          .
        </p>
      </Card>
    </Page>
  );
}

/** A page that breaks shows a way back instead of a blank screen. */
class PageBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("page failed", error, info.componentStack);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <AuthCard title="Something went wrong" sub="This page hit a problem. Nothing you saved is lost.">
        <button type="button" className="btn btn--accent btn--lg btn--block" onClick={() => window.location.reload()}>
          Reload
        </button>
        <a className="btn btn--lg btn--block signin__second" href="/">
          Go to Home
        </a>
      </AuthCard>
    );
  }
}

/** A page that failed to load or draw: a way back instead of the router's own error screen. */
function RouteError({ inline }: { inline?: boolean }) {
  const err = useRouteError();
  console.error("page failed", err);
  // After a new version goes out, the old pages' files are gone: load the new version once instead of failing.
  if (err instanceof Error && /dynamically imported module|Importing a module script failed|error loading dynamically/i.test(err.message)) {
    let recent = true;
    try {
      recent = Date.now() - Number(sessionStorage.getItem("mivio.reloaded") ?? 0) < 60_000;
      if (!recent) sessionStorage.setItem("mivio.reloaded", String(Date.now()));
    } catch { recent = true; }
    if (!recent) { window.location.reload(); return null; }
  }
  if (inline) {
    return (
      <Page title="Something went wrong" narrow>
        <Card>
          <p className="acct-plain">This page hit a problem. Nothing you saved is lost.</p>
          <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </Card>
      </Page>
    );
  }
  return (
    <AuthCard title="Something went wrong" sub="This page hit a problem. Nothing you saved is lost.">
      <button type="button" className="btn btn--accent btn--lg btn--block" onClick={() => window.location.reload()}>
        Reload
      </button>
      <a className="btn btn--lg btn--block signin__second" href="/">
        Go to Home
      </a>
    </AuthCard>
  );
}

function toSignIn() {
  const here = window.location.pathname + window.location.search;
  go(`/sign-in${here === "/" ? "" : `?return=${encodeURIComponent(here)}`}`);
}

/** Signed in, or off to log in (and back here after). */
function useSignedIn(): boolean {
  const { me, ready } = useSession();
  useEffect(() => {
    if (ready && !me) toSignIn();
  }, [ready, me]);
  return ready && !!me;
}

function Loading() {
  return <div className="loading">Loading…</div>;
}

/* ---------- above any business ---------- */

function PublicOnly() {
  const { me, ready } = useSession();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (me) {
    go(safeReturn(new URLSearchParams(loc.search).get("return")));
    return <Loading />;
  }
  return <Outlet />;
}

function OuterGate() {
  const ok = useSignedIn();
  if (!ok) return <Loading />;
  return (
    <OuterShell>
      <Outlet />
    </OuterShell>
  );
}

const outerRoutes: RouteObject[] = [
  {
    errorElement: <RouteError />,
    element: <PublicOnly />,
    children: [
      { path: "sign-in", element: <SignInPage /> },
      { path: "sign-up", element: <SignUpPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
    ],
  },
  { path: "invite/:token", element: <InvitePage />, errorElement: <RouteError /> },
  {
    errorElement: <RouteError />,
    element: <OuterGate />,
    children: [
      { index: true, element: <WelcomePage /> },
      { path: "account", element: <Navigate to="/account/general" replace /> },
      { path: "account/:tab", element: <AccountPage /> },
      { path: "new", element: <NewBusinessPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

function OuterApp() {
  const router = useMemo(() => createBrowserRouter(outerRoutes), []);
  return <RouterProvider router={router} />;
}

/* ---------- inside a business ---------- */

function businessRoutes(): RouteObject[] {
  const pages: RouteObject[] = SOLUTION_ROUTES.filter((r) => !r.bare).map((r) => ({ path: r.path, element: <SolutionGate solution={r.solution}>{r.element}</SolutionGate> }));
  const bare: RouteObject[] = SOLUTION_ROUTES.filter((r) => r.bare).map((r) => ({ path: r.path, element: <SolutionGate solution={r.solution}>{r.element}</SolutionGate> }));
  return [
    {
      element: <BareFrame />,
      errorElement: <RouteError />,
      children: bare,
    },
    {
      element: <Shell />,
      errorElement: <RouteError />,
      children: [
        {
          errorElement: <RouteError inline />,
          children: [
            { index: true, element: <HomePage /> },
            { path: "account", element: <Navigate to="/account/general" replace /> },
            { path: "account/:tab", element: <AccountPage /> },
            { path: "settings", element: <SettingsPage /> },
            { path: "settings/*", element: <SettingsPage /> },
            { path: "agents", element: later(<AgentsPage />) },
            { path: "agents/approvals", element: later(<ApprovalsPage />) },
            { path: "agents/approvals/:id", element: later(<ApprovalPage />) },
            { path: "agents/history", element: later(<AgentsHistoryPage />) },
            { path: "agents/settings", element: later(<GuardrailsPage />) },
            ...pages,
            { path: "*", element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ];
}

/** A page on its own (phone pages at the door): no sidebar, still signed in. */
function BareFrame() {
  return (
    <>
      <Outlet />
      <ToastHost />
    </>
  );
}

/** A solution's page shows only where the solution is switched on. */
function SolutionGate({ solution, children }: { solution: string; children: ReactNode }) {
  const { has } = useBusiness();
  if (!has(solution)) return <NotFoundPage />;
  // Each solution's pages load when first opened.
  return <Suspense fallback={<div className="loading">Loading…</div>}>{children}</Suspense>;
}

function BusinessApp({ handle }: { handle: string }) {
  const ok = useSignedIn();
  const ov = useOverviewQuery(handle);
  const router = useMemo(() => createBrowserRouter(businessRoutes(), { basename: `/${handle}` }), [handle]);
  const { refetch } = ov;
  const reload = useCallback(() => void refetch(), [refetch]);
  useEffect(() => {
    if (!ov.data) return;
    setBusinessClock(ov.data.business.timeZone, ov.data.business.locale);
    rememberBusiness(handle);
    document.title = `${ov.data.business.name} · Mivio`;
  }, [ov.data, handle]);
  if (!ok) return <Loading />;
  if (ov.isPending) return <Loading />;
  if (ov.isError || !ov.data?.business) {
    const missing = ov.error instanceof ApiError && (ov.error.status === 404 || ov.error.status === 403);
    return (
      <AuthCard title={missing ? "Business not found" : "This business didn't open"} sub={missing ? "It doesn't exist, or you're not part of it." : (ov.error as Error | null)?.message}>
        <a className="btn btn--accent btn--lg btn--block" href="/">
          Go to your businesses
        </a>
      </AuthCard>
    );
  }
  setBusinessClock(ov.data.business.timeZone, ov.data.business.locale);
  return (
    <BusinessProvider data={ov.data} reload={reload}>
      <RouterProvider router={router} />
    </BusinessProvider>
  );
}

export function App() {
  const first = (window.location.pathname.split("/")[1] ?? "").toLowerCase();
  const inBusiness = !OUTER.has(first) && HANDLE.test(first);
  if (inBusiness) setBusinessHandle(first);
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <PageBoundary>{inBusiness ? <BusinessApp handle={first} /> : <OuterApp />}</PageBoundary>
      </SessionProvider>
    </QueryClientProvider>
  );
}
